import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocketContext } from '../contexts/WebSocketContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LeadKanbanBoard from '../components/LeadKanbanBoard';
import LeadListView from '../components/LeadListView';
import UpdateLeadModal from '../components/UpdateLeadModal';
import PricingSuggestionModal from '../components/PricingSuggestionModal';
import { LayoutGrid, List as ListIcon, Plus, Filter, Download, Trash2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Use native XMLHttpRequest to bypass rrweb-recorder fetch wrapper
const nativeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    
    // Set headers
    if (options.headers) {
      Object.keys(options.headers).forEach(key => {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: true, data, status: xhr.status });
        } catch (e) {
          resolve({ ok: true, data: xhr.responseText, status: xhr.status });
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error'));
    };
    
    // Send request
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

const STAGE_OPTIONS = [
  { value: 'new', label: 'New', color: '#3b82f6' },
  { value: 'contacted', label: 'Contacted', color: '#8b5cf6' },
  { value: 'qualified', label: 'Qualified', color: '#06b6d4' },
  { value: 'consultation_scheduled', label: 'Consultation Scheduled', color: '#10b981' },
  { value: 'consultation_completed', label: 'Consultation Completed', color: '#f59e0b' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: '#f97316' },
  { value: 'agreement_sent', label: 'Agreement Sent', color: '#ef4444' },
  { value: 'agreement_signed', label: 'Agreement Signed', color: '#14b8a6' },
  { value: 'payment_partial', label: 'Partial Payment', color: '#84cc16' },
  { value: 'payment_full', label: 'Payment Full (WON)', color: '#22c55e' },
  { value: 'lost', label: 'Lost', color: '#6b7280' },
  { value: 'nurture', label: 'Nurture', color: '#a855f7' }
];

const TEMPERATURE_OPTIONS = [
  { value: 'hot', label: '🔥 Hot', color: '#ef4444' },
  { value: 'warm', label: '☀️ Warm', color: '#f59e0b' },
  { value: 'cold', label: '❄️ Cold', color: '#3b82f6' }
];

export default function LeadManagement() {
  const { user } = useAuth();
  const { refreshTriggers } = useWebSocketContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  
  // Remove padding and set overflow on parent admin-content
  useEffect(() => {
    const adminContent = document.querySelector('.admin-content');
    if (adminContent) {
      adminContent.style.padding = '0';
      adminContent.style.overflow = 'hidden';
      adminContent.style.height = '100%';
    }
    
    return () => {
      // Restore original styles on unmount
      if (adminContent) {
        adminContent.style.padding = '';
        adminContent.style.overflow = '';
        adminContent.style.height = '';
      }
    };
  }, []);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningLead, setAssigningLead] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [filters, setFilters] = useState({
    stage: '',
    temperature: '',
    source: '',
    from_date: '', // No default - show all leads
    to_date: '', // No default - show all leads
    search: '', // Search query
    unattended_only: false // Filter for unattended leads
  });
  
  const [unattendedStats, setUnattendedStats] = useState({
    unattended_count: 0,
    unattended_lead_ids: []
  });
  const [pipelineStats, setPipelineStats] = useState(null);
  const [services, setServices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [schengenCountries, setSchengenCountries] = useState([]);
  const [showSchengenSelector, setShowSchengenSelector] = useState(false);
  const [showOtherCountryInput, setShowOtherCountryInput] = useState(false);
  const [leadStages, setLeadStages] = useState([]);
  const [userRolePermissions, setUserRolePermissions] = useState({
    accessible_stages: [],
    viewable_stages: [],
    role_key: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    destination_country: '',
    schengen_country: '',
    other_country: '',
    num_applicants: 1,
    travel_date: '',
    source: 'website',
    temperature: 'warm',
    notes: '',
    auto_email_enabled: true
  });

  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: '',
    transaction_id: '',
    notes: ''
  });

  // Check URL parameters to apply filters from GlobalStatsBar clicks
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam === 'unattended') {
      setFilters(prev => ({
        ...prev,
        stage: '',
        temperature: '',
        source: '',
        from_date: '',
        to_date: '',
        search: '',
        unattended_only: true
      }));
      // Clear URL parameter after applying
      setSearchParams({});
    } else if (filterParam === 'to-work') {
      // Show all non-cancelled leads
      setFilters({
        stage: '',
        temperature: '',
        source: '',
        from_date: '',
        to_date: '',
        search: '',
        unattended_only: false
      });
      // Clear URL parameter after applying
      setSearchParams({});
    } else if (filterParam === 'cancelled') {
      // Show only cancelled leads
      setFilters({
        stage: 'cancelled',
        temperature: '',
        source: '',
        from_date: '',
        to_date: '',
        search: '',
        unattended_only: false
      });
      // Clear URL parameter after applying
      setSearchParams({});
    } else if (filterParam === 'all') {
      // Legacy support - same as to-work
      setFilters({
        stage: '',
        temperature: '',
        source: '',
        from_date: '',
        to_date: '',
        search: '',
        unattended_only: false
      });
      // Clear URL parameter after applying
      setSearchParams({});
    }
  }, [searchParams]); // Watch for URL param changes

  useEffect(() => {
    // Initialize admin data from localStorage
    const adminData = localStorage.getItem('admin_data');
    if (adminData) {
      setAdmin(JSON.parse(adminData));
    }
    
    fetchLeads();
    fetchUnattendedStats();
    fetchPipelineStats();
    fetchServices();
    fetchCountries();
    fetchSchengenCountries();
    fetchLeadStages();
    fetchUserRolePermissions();
    fetchSources();
  }, [filters, refreshTriggers.leads]); // Auto-refresh when WebSocket triggers update

  // Listen for filter changes from GlobalStatsBar (legacy support)
  useEffect(() => {
    const handleFilterChange = (event) => {
      setFilters(event.detail);
    };
    
    window.addEventListener('applyLeadFilter', handleFilterChange);
    
    return () => {
      window.removeEventListener('applyLeadFilter', handleFilterChange);
    };
  }, []);

  // Effect to handle opening update modal from notification
  useEffect(() => {
    const leadId = searchParams.get('lead_id');
    const openUpdate = searchParams.get('openUpdate');
    
    if (leadId && openUpdate === 'true') {
      // Try to find the lead in current list
      let lead = leads.find(l => l.id === leadId);
      
      if (lead) {
        setSelectedLead(lead);
        setShowUpdateModal(true); // CRITICAL: Must set this to show modal
        // Clear the URL parameters after opening modal
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('lead_id');
        newParams.delete('openUpdate');
        setSearchParams(newParams);
      } else if (leads.length > 0) {
        // Lead not found in current filtered list, fetch it directly
        const fetchSpecificLead = async () => {
          try {
            const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
            const response = await nativeFetch(
              `${BACKEND_URL}/api/leads/${leadId}`,
              {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            
            if (response.ok && response.data) {
              setSelectedLead(response.data);
              setShowUpdateModal(true); // CRITICAL: Must set this to show modal
              // Clear the URL parameters
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('lead_id');
              newParams.delete('openUpdate');
              setSearchParams(newParams);
            }
          } catch (error) {
            console.error('Error fetching lead:', error);
          }
        };
        
        fetchSpecificLead();
      }
    }
  }, [leads, searchParams]);

  // Effect to handle form population when editing a lead
  useEffect(() => {
    if (showCreateModal && selectedLead) {
      // Populate form with selected lead data
      setFormData({
        name: selectedLead.name || '',
        email: selectedLead.email || '',
        phone: selectedLead.phone || '',
        service_type: selectedLead.service_type || '',
        destination_country: selectedLead.destination_country || '',
        schengen_country: selectedLead.schengen_country || '',
        other_country: selectedLead.other_country || '',
        num_applicants: selectedLead.num_applicants || 1,
        travel_date: selectedLead.travel_date || '',
        source: selectedLead.source || 'website',
        temperature: selectedLead.temperature || 'warm',
        notes: selectedLead.notes || ''
      });
      
      // Set appropriate selector visibility
      if (selectedLead.destination_country === 'Schengen (Europe)') {
        setShowSchengenSelector(true);
        setShowOtherCountryInput(false);
      } else if (selectedLead.destination_country === 'Other') {
        setShowSchengenSelector(false);
        setShowOtherCountryInput(true);
      } else {
        setShowSchengenSelector(false);
        setShowOtherCountryInput(false);
      }
    }
  }, [showCreateModal, selectedLead]);

  const fetchServices = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/services`);
      if (result.ok) {
        setServices(result.data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/countries`);
      if (result.ok) {
        setCountries(result.data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchSchengenCountries = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/schengen-countries`);
      if (result.ok) {
        setSchengenCountries(result.data);
      }
    } catch (error) {
      console.error('Error fetching Schengen countries:', error);
    }
  };

  const fetchLeadStages = async () => {
    try {
      const result = await nativeFetch(
        `${BACKEND_URL}/api/lead-stages`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      if (result.ok) {
        setLeadStages(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching lead stages:', error);
    }
  };

  const fetchUserRolePermissions = async () => {
    try {
      // Get current user's role from localStorage
      const adminData = localStorage.getItem('admin_data');
      if (!adminData) return;
      
      const parsedData = JSON.parse(adminData);
      const currentUserRole = parsedData.role;
      
      // Fetch all roles to get permissions
      const result = await nativeFetch(
        `${BACKEND_URL}/api/admin/roles`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      
      if (result.ok) {
        // Find current user's role object
        const roleObj = (result.data || []).find(r => r.role_key === currentUserRole);
        
        if (roleObj) {
          // Super Admin gets all stages
          if (currentUserRole === 'super_admin') {
            // Fetch all stages for super admin
            const stagesResult = await nativeFetch(
              `${BACKEND_URL}/api/lead-stages`,
              {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                }
              }
            );
            if (stagesResult.ok) {
              const allStageKeys = (stagesResult.data || []).map(s => s.stage_key);
              const permissions = {
                accessible_stages: allStageKeys,
                viewable_stages: [],
                role_key: currentUserRole
              };
              setUserRolePermissions(permissions);
            }
          } else {
            setUserRolePermissions({
              accessible_stages: roleObj.accessible_stages || [],
              viewable_stages: roleObj.viewable_stages || [],
              role_key: currentUserRole
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user role permissions:', error);
    }
  };

  const fetchSources = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/sources/active`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (result.ok) {
        const sourcesData = result.data.map(source => ({
          value: source.code,
          label: source.name
        }));
        setSourceOptions(sourcesData);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
      // Fallback to basic sources if API fails
      setSourceOptions([
        { value: 'website', label: 'Website' },
        { value: 'referral', label: 'Referral' }
      ]);
    }
  };

  const fetchLeads = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.stage) queryParams.append('stage', filters.stage);
      if (filters.temperature) queryParams.append('temperature', filters.temperature);
      if (filters.source) queryParams.append('source', filters.source);
      if (filters.from_date) queryParams.append('from_date', filters.from_date);
      if (filters.to_date) queryParams.append('to_date', filters.to_date);
      if (filters.search) queryParams.append('search', filters.search);
      
      // Don't show cancelled/duplicate leads by default ONLY when no stage is selected
      if (!filters.stage) {
        queryParams.append('exclude_cancelled', 'true');
      }
      
      // If unattended filter is active, fetch unattended lead IDs first
      const isUnattendedFilter = filters.unattended_only;
      let unattendedLeadIds = [];
      
      if (isUnattendedFilter) {
        try {
          const statsResult = await nativeFetch(`${BACKEND_URL}/api/leads/stats/unattended-new-leads`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
            }
          });
          if (statsResult.ok && statsResult.data) {
            unattendedLeadIds = statsResult.data.unattended_lead_ids || [];
          }
        } catch (error) {
          console.error('Error fetching unattended lead IDs:', error);
        }
      }

      const result = await nativeFetch(
        `${BACKEND_URL}/api/leads?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (result.ok) {
        // If modal is open, fetch fresh selected lead data FIRST before updating leads list
        // This prevents the modal from closing due to stale selectedLead reference
        if (selectedLead && selectedLead.id && showUpdateModal) {
          try {
            const leadResponse = await fetch(`${BACKEND_URL}/api/leads/${selectedLead.id}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
              }
            });
            if (leadResponse.ok) {
              const freshLead = await leadResponse.json();
              // Update both states atomically
              setSelectedLead(freshLead);
              
              let fetchedLeads = result.data || [];
              
              // Apply unattended filter if active
              if (isUnattendedFilter && unattendedLeadIds && unattendedLeadIds.length > 0) {
                fetchedLeads = fetchedLeads.filter(lead => 
                  unattendedLeadIds.includes(lead.id)
                );
              }
              
              setLeads(fetchedLeads);
            } else {
              // If lead no longer exists, close modal and update list
              setShowUpdateModal(false);
              setSelectedLead(null);
              
              let fetchedLeads = result.data || [];
              
              // Apply unattended filter if active
              if (isUnattendedFilter && unattendedLeadIds && unattendedLeadIds.length > 0) {
                fetchedLeads = fetchedLeads.filter(lead => 
                  unattendedLeadIds.includes(lead.id)
                );
              }
              
              setLeads(fetchedLeads);
            }
          } catch (err) {
            console.error('Error refreshing selected lead:', err);
            // On error, still update the leads list
            let fetchedLeads = result.data || [];
            
            // Apply unattended filter if active
            if (isUnattendedFilter && unattendedLeadIds && unattendedLeadIds.length > 0) {
              fetchedLeads = fetchedLeads.filter(lead => 
                unattendedLeadIds.includes(lead.id)
              );
            }
            
            setLeads(fetchedLeads);
          }
        } else {
          // No modal open, just update leads
          let fetchedLeads = result.data || [];
          
          // Apply unattended filter if active
          if (isUnattendedFilter && unattendedLeadIds && unattendedLeadIds.length > 0) {
            fetchedLeads = fetchedLeads.filter(lead => 
              unattendedLeadIds.includes(lead.id)
            );
          }
          
          setLeads(fetchedLeads);
        }
      } else {
        console.error('Failed to fetch leads:', result.error);
        setLeads([]);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPipelineStats = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/leads/stats/pipeline`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (result.ok) {
        setPipelineStats(result.data);
      }
    } catch (error) {
      console.error('Error fetching pipeline stats:', error);
    }
  };

  const fetchUnattendedStats = async () => {
    try {
      const result = await nativeFetch(`${BACKEND_URL}/api/leads/stats/unattended-new-leads`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (result.ok && result.data) {
        setUnattendedStats(result.data);
      }
    } catch (error) {
      console.error('Error fetching unattended stats:', error);
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    
    // Validate "Other" country if selected
    if (formData.destination_country === 'Other' && formData.other_country) {
      try {
        const validationResult = await nativeFetch(`${BACKEND_URL}/api/validate-country`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: formData.other_country })
        });
        
        if (validationResult.ok && validationResult.data) {
          if (!validationResult.data.valid) {
            alert(validationResult.data.message || 'Please enter a valid country name');
            return;
          }
          // Update with normalized (title case) name
          formData.other_country = validationResult.data.normalized_name;
        }
      } catch (error) {
        console.error('Error validating country:', error);
        // Continue anyway if validation fails
      }
    }
    
    try {
      // If selectedLead exists, we're editing; otherwise creating
      const url = selectedLead 
        ? `${BACKEND_URL}/api/leads/${selectedLead.id}`
        : `${BACKEND_URL}/api/leads`;
      
      const method = selectedLead ? 'PUT' : 'POST';
      
      const response = await nativeFetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowCreateModal(false);
        setSelectedLead(null);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service_type: '',
          destination_country: '',
          schengen_country: '',
          other_country: '',
          num_applicants: 1,
          travel_date: '',
          source: 'website',
          temperature: 'warm',
          notes: ''
        });
        setShowSchengenSelector(false);
        setShowOtherCountryInput(false);
        fetchLeads();
        fetchPipelineStats();
      }
    } catch (error) {
      console.error('Error saving lead:', error);
    }
  };

  const handleUpdateStage = async (leadId, newStage) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({ stage: newStage })
      });

      if (response.ok) {
        fetchLeads();
        fetchPipelineStats();
        if (selectedLead && selectedLead.id === leadId) {
          const updatedLead = await response.json();
          setSelectedLead(updatedLead);
        }
      }
    } catch (error) {
      console.error('Error updating stage:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const result = await nativeFetch(
        `${BACKEND_URL}/api/users`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );
      if (result.ok) {
        setAvailableUsers(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const openAssignModal = (lead) => {
    setAssigningLead(lead);
    setShowAssignModal(true);
    fetchUsers();
  };

  const handleAssignLead = async (userId, reason) => {
    try {
      const result = await nativeFetch(
        `${BACKEND_URL}/api/admin/leads/${assigningLead.id}/assign`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          },
          body: JSON.stringify({ assigned_to: userId, reason })
        }
      );
      
      if (result.ok) {
        alert('Lead assigned successfully!');
        setShowAssignModal(false);
        setAssigningLead(null);
        fetchLeads();
      } else {
        alert('Failed to assign lead');
      }
    } catch (error) {
      console.error('Error assigning lead:', error);
      alert('Error assigning lead');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/leads/${selectedLead.id}/payments?amount=${paymentData.amount}&payment_method=${paymentData.payment_method || ''}&transaction_id=${paymentData.transaction_id || ''}&notes=${paymentData.notes || ''}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (response.ok) {
        setShowPaymentModal(false);
        setPaymentData({ amount: '', payment_method: '', transaction_id: '', notes: '' });
        fetchLeads();
        fetchPipelineStats();
        // Refresh lead details
        const leadResponse = await fetch(`${BACKEND_URL}/api/leads/${selectedLead.id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
        });
        if (leadResponse.ok) {
          const updatedLead = await leadResponse.json();
          setSelectedLead(updatedLead);
        }
      }
    } catch (error) {
      console.error('Error adding payment:', error);
    }
  };

  const handleConvertToClient = async (leadId) => {
    const processConsultant = prompt('Enter username of Process Consultant to assign:');
    if (!processConsultant) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/leads/${leadId}/convert-to-client?process_consultant=${processConsultant}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(`Lead converted to client successfully! Client ID: ${data.client_id}`);
        setShowDetailModal(false);
        fetchLeads();
        fetchPipelineStats();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error converting to client:', error);
    }
  };

  const handleViewDetails = async (lead) => {
    try {
      // Use nativeFetch to bypass rrweb-recorder fetch wrapper that causes "Response body already used" error
      const result = await nativeFetch(`${BACKEND_URL}/api/leads/${lead.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (result.ok) {
        setSelectedLead(result.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
    }
  };

  const handleUpdateLead = async (lead) => {
    // Always fetch fresh lead data before editing to ensure all fields are current
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${lead.id}`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const freshLead = await response.json();
        setSelectedLead(freshLead);
        setShowUpdateModal(true);
      } else {
        console.error('Failed to fetch fresh lead data');
        // Fallback to using the lead passed in
        setSelectedLead(lead);
        setShowUpdateModal(true);
      }
    } catch (error) {
      console.error('Error fetching fresh lead data:', error);
      // Fallback to using the lead passed in
      setSelectedLead(lead);
      setShowUpdateModal(true);
    }
  };

  const handleGetPricing = (lead) => {
    setSelectedLead(lead);
    setShowPricingModal(true);
  };

  const getAuthHeader = () => {
    return {
      'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
    };
  };

  const handleStageChange = async (leadId, newStage) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({ stage: newStage })
      });

      if (response.ok) {
        fetchLeads();
        // Optionally show success message
      }
    } catch (error) {
      console.error('Error updating lead stage:', error);
    }
  };

  const handleDelete = async (leadId) => {
    const confirmed = await window.confirm('Are you sure you want to delete this lead?');
    if (!confirmed) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.ok) {
        fetchLeads();
      }
    } catch (error) {
      console.error('Error deleting lead:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeadIds.length === 0) {
      alert('Please select leads to delete');
      return;
    }
    
    const confirmed = await window.confirm(`Are you sure you want to delete ${selectedLeadIds.length} lead(s)?`);
    if (!confirmed) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(selectedLeadIds)
      });

      if (response.ok) {
        setSelectedLeadIds([]);
        fetchLeads();
      }
    } catch (error) {
      console.error('Error bulk deleting leads:', error);
    }
  };

  const handleBulkCancel = async () => {
    if (selectedLeadIds.length === 0) {
      alert('Please select leads to cancel');
      return;
    }
    
    const confirmed = await window.confirm(`Are you sure you want to cancel ${selectedLeadIds.length} lead(s)?`);
    if (!confirmed) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/leads/bulk-cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(selectedLeadIds)
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setSelectedLeadIds([]);
        fetchLeads();
      }
    } catch (error) {
      console.error('Error bulk cancelling leads:', error);
    }
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLeadIds.length === leads.length) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(leads.map(lead => lead.id));
    }
  };

  // Helper function to get color for each stage
  function getStageColorByKey(stageKey) {
    const colorMap = {
      'enquiry': '#3b82f6',
      'qualified': '#06b6d4',
      'pfnoc': '#8b5cf6',
      'proposal_sent': '#f97316',
      'proposal_approved': '#10b981',
      'agreement_sent': '#ef4444',
      'agreement_signed': '#14b8a6',
      'payment_confirmation': '#84cc16',
      'welcome_call': '#8b5cf6',
      'document_collection': '#10b981',
      'application_preparation': '#f59e0b',
      'application_submitted': '#ec4899',
      'under_review': '#a855f7',
      'approved': '#22c55e',
      'delivered': '#14b8a6',
      'on_hold': '#f59e0b',
      'rejected': '#ef4444',
      'cancelled': '#6b7280'
    };
    return colorMap[stageKey] || '#94a3b8';
  }

  // Compute filtered stage options based on user role permissions
  const filteredStageOptions = leadStages
    .filter(stage => {
      // Super admin sees all stages
      if (userRolePermissions.role_key === 'super_admin') return true;
      // Check if stage is accessible or viewable
      return userRolePermissions.accessible_stages.includes(stage.stage_key) || 
             userRolePermissions.viewable_stages.includes(stage.stage_key);
    })
    .map(stage => ({
      value: stage.stage_key,
      label: stage.stage_label,
      color: getStageColorByKey(stage.stage_key),
      isReadOnly: userRolePermissions.viewable_stages.includes(stage.stage_key) && 
                  !userRolePermissions.accessible_stages.includes(stage.stage_key)
    }))
    .sort((a, b) => {
      // Sort by stage order
      const stageA = leadStages.find(s => s.stage_key === a.value);
      const stageB = leadStages.find(s => s.stage_key === b.value);
      return (stageA?.stage_order || 0) - (stageB?.stage_order || 0);
    });

  const getStageColor = (stage) => {
    const stageOption = filteredStageOptions.find(s => s.value === stage);
    return stageOption ? stageOption.color : '#94a3b8';
  };

  // Render stats in header using portal
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 70px)', overflow: 'hidden' }}>
      {/* Sticky Header and Filters */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div className="page-header">
        <div>
          <h1>Lead Management</h1>
        </div>
        <div className="page-header-actions" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {/* View Toggle */}
          <div className="view-toggle-container">
            <button
              className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              <LayoutGrid size={18} />
              <span>Kanban</span>
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <ListIcon size={18} />
              <span>List</span>
            </button>
          </div>
          
          <div className="action-buttons-group" style={{ display: 'flex', gap: '0.75rem' }}>
            {(admin?.role === 'super_admin' || admin?.role === 'admin') && selectedLeadIds.length > 0 && (
              <>
                <button className="btn-danger" onClick={handleBulkCancel} style={{ background: '#d97706', borderColor: '#d97706' }} data-testid="bulk-cancel-btn">
                  <XCircle size={18} />
                  <span>Cancel Selected ({selectedLeadIds.length})</span>
                </button>
                {admin?.role === 'super_admin' && (
                  <button className="btn-danger" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                    <Trash2 size={18} />
                    <span>Delete Selected ({selectedLeadIds.length})</span>
                  </button>
                )}
              </>
            )}
            
            <button className="btn-primary create-lead-btn" onClick={() => {
              // Reset form data to blank when creating new lead
              setFormData({
                name: '',
                email: '',
                phone: '',
                source: 'Website',
                service_type: '',
                destination_country: '',
                travel_date: '',
                num_applicants: 1,
                temperature: 'warm',
                stage: 'new'
              });
              setSelectedLead(null);
              setShowCreateModal(true);
            }}>
              <Plus size={18} />
              <span>Create Lead</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar" style={{ 
        display: 'flex', 
        gap: '8px', 
        alignItems: 'center', 
        flexWrap: 'wrap',
        padding: '10px 12px',
        background: '#f9fafb',
        borderRadius: '6px',
        marginBottom: '12px'
      }}>
        {/* Date Filters */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', minWidth: '35px' }}>From:</label>
          <input
            type="date"
            value={filters.from_date}
            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            className="filter-select"
            style={{ minWidth: '130px', padding: '4px 8px', fontSize: '12px', height: '30px' }}
          />
        </div>
        
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', minWidth: '25px' }}>To:</label>
          <input
            type="date"
            value={filters.to_date}
            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            className="filter-select"
            style={{ minWidth: '130px', padding: '4px 8px', fontSize: '12px', height: '30px' }}
          />
        </div>
        
        <button
          onClick={() => setFilters({
            stage: '',
            temperature: '',
            source: '',
            from_date: '',
            to_date: ''
          })}
          style={{
            padding: '5px 10px',
            background: '#6b7280',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            height: '30px'
          }}
        >
          Clear All
        </button>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', background: '#d1d5db' }}></div>

        {/* Other Filters */}
        <select
          value={filters.stage}
          onChange={(e) => setFilters({ ...filters, stage: e.target.value })}
          className="filter-select"
          style={{ minWidth: '130px', padding: '4px 8px', fontSize: '12px', height: '30px' }}
        >
          <option key="all-stages" value="">All Stages</option>
          {filteredStageOptions.map(stage => (
            <option key={stage.value} value={stage.value}>{stage.label}</option>
          ))}
        </select>

        <select
          value={filters.temperature}
          onChange={(e) => setFilters({ ...filters, temperature: e.target.value })}
          className="filter-select"
          style={{ minWidth: '130px', padding: '4px 8px', fontSize: '12px', height: '30px' }}
        >
          <option key="all-temps" value="">All Temperatures</option>
          {TEMPERATURE_OPTIONS.map(temp => (
            <option key={temp.value} value={temp.value}>{temp.label}</option>
          ))}
        </select>

        <select
          value={filters.source}
          onChange={(e) => setFilters({ ...filters, source: e.target.value })}
          className="filter-select"
          style={{ minWidth: '130px', padding: '4px 8px', fontSize: '12px', height: '30px' }}
        >
          <option key="all-sources" value="">All Sources</option>
          {sourceOptions.map(source => (
            <option key={source.value} value={source.value}>{source.label}</option>
          ))}
        </select>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', background: '#d1d5db' }}></div>

        {/* Search Input */}
        <input
          type="text"
          placeholder="Search by name, phone, email, service..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="filter-select"
          style={{ 
            minWidth: '280px', 
            padding: '4px 12px', 
            fontSize: '12px', 
            height: '30px',
            border: '1px solid #d1d5db',
            borderRadius: '4px'
          }}
        />
      </div>

      {/* Column Headers for List View (Sticky) */}
      {!loading && viewMode === 'list' && (
        <div style={{ 
          background: 'white', 
          borderBottom: '2px solid #e5e7eb'
        }}>
          <table className="list-view-table" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                {admin?.role === 'super_admin' && (
                  <th className="list-th" style={{ width: '50px' }}>
                    <input 
                      type="checkbox" 
                      checked={leads.length > 0 && selectedLeadIds.length === leads.length}
                      onChange={toggleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                <th className="list-th sticky-col">Lead</th>
                <th className="list-th">Contact</th>
                <th className="list-th">Service</th>
                <th className="list-th">Source</th>
                <th className="list-th">Temperature</th>
                <th className="list-th">Stage</th>
                <th className="list-th">Assigned To</th>
                <th className="list-th">Created</th>
                <th className="list-th list-th-actions">Actions</th>
              </tr>
            </thead>
          </table>
        </div>
      )}
      </div>

      {/* Scrollable Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '0 0 20px 0' }}>
        {/* Leads View */}
      {loading ? (
        <div className="text-center" style={{ padding: '4rem 2rem' }}>Loading...</div>
      ) : viewMode === 'kanban' ? (
        <LeadKanbanBoard
          leads={leads}
          stages={filteredStageOptions}
          onUpdateStage={handleStageChange}
          onViewDetails={handleViewDetails}
          onUpdateLead={handleUpdateLead}
          onAssignLead={openAssignModal}
          onEditLead={(lead) => {
            setSelectedLead(lead);
            setFormData({
              name: lead.name,
              email: lead.email,
              phone: lead.phone,
              source: lead.source,
              service_type: lead.service_type || '',
              destination_country: lead.destination_country || '',
              travel_date: lead.travel_date || '',
              num_applicants: lead.num_applicants || 1,
              temperature: lead.temperature,
              stage: lead.stage,
              auto_email_enabled: lead.auto_email_enabled !== false
            });
            setShowCreateModal(true);
          }}
        />
      ) : (
        <LeadListView
          leads={leads}
          onViewDetails={handleViewDetails}
          onUpdateLead={handleUpdateLead}
          onAssignLead={openAssignModal}
          onDeleteLead={admin?.role === 'super_admin' ? handleDelete : null}
          onGetPricing={handleGetPricing}
          selectedLeadIds={selectedLeadIds}
          onToggleSelect={toggleLeadSelection}
          onToggleSelectAll={toggleSelectAll}
          showSelection={admin?.role === 'super_admin' || admin?.role === 'admin'}
          onEditLead={(lead) => {
            setSelectedLead(lead);
            setFormData({
              name: lead.name,
              email: lead.email,
              phone: lead.phone,
              source: lead.source,
              service_type: lead.service_type || '',
              destination_country: lead.destination_country || '',
              travel_date: lead.travel_date || '',
              num_applicants: lead.num_applicants || 1,
              temperature: lead.temperature,
              stage: lead.stage,
              auto_email_enabled: lead.auto_email_enabled !== false
            });
            setShowCreateModal(true);
          }}
          getStageColor={getStageColor}
          STAGE_OPTIONS={filteredStageOptions}
          TEMPERATURE_OPTIONS={TEMPERATURE_OPTIONS}
          hideHeader={true}
        />
      )}

      {/* Create/Edit Lead Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{selectedLead ? 'Edit Lead' : 'Create New Lead'}</h2>
            <form onSubmit={handleCreateLead}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Service Type *</label>
                  <select
                    value={formData.service_type}
                    onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    required
                  >
                    <option key="select-service" value="">Select Service</option>
                    {services.map(service => (
                      <option key={service.id} value={service.title}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Destination Country *</label>
                  <select
                    value={formData.destination_country}
                    onChange={(e) => {
                      const selectedCountry = e.target.value;
                      setFormData({ 
                        ...formData, 
                        destination_country: selectedCountry,
                        schengen_country: selectedCountry === 'Schengen (Europe)' ? formData.schengen_country : '',
                        other_country: selectedCountry === 'Other' ? formData.other_country : ''
                      });
                      // Show Schengen selector if Schengen (Europe) is selected
                      setShowSchengenSelector(selectedCountry === 'Schengen (Europe)');
                      // Show Other country input if Other is selected
                      setShowOtherCountryInput(selectedCountry === 'Other');
                    }}
                    required
                  >
                    <option key="select-country" value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.name} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Schengen Country Selector - appears when Schengen (Europe) is selected */}
                {showSchengenSelector && (
                  <div className="form-group" style={{ background: '#f0f9ff', padding: '12px', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                    <label>Select Specific Schengen Country *</label>
                    <select
                      value={formData.schengen_country || ''}
                      onChange={(e) => setFormData({ ...formData, schengen_country: e.target.value })}
                      required
                      style={{ background: 'white' }}
                    >
                      <option value="">Select Schengen Country</option>
                      {schengenCountries.map(country => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <small style={{ display: 'block', marginTop: '6px', color: '#0369a1', fontSize: '12px' }}>
                      💡 Select the primary destination country within Schengen area
                    </small>
                  </div>
                )}

                {/* Other Country Input - appears when Other is selected */}
                {showOtherCountryInput && (
                  <div className="form-group" style={{ background: '#fef3c7', padding: '12px', borderRadius: '6px', border: '1px solid #fcd34d' }}>
                    <label>Specify Country Name *</label>
                    <input
                      type="text"
                      value={formData.other_country || ''}
                      onChange={(e) => setFormData({ ...formData, other_country: e.target.value })}
                      placeholder="Enter country name..."
                      required
                      style={{ background: 'white', width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '6px', color: '#92400e', fontSize: '12px' }}>
                      💡 Please enter the destination country not listed above
                    </small>
                  </div>
                )}
                <div className="form-group">
                  <label>Number of Applicants *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.num_applicants}
                    onChange={(e) => setFormData({ ...formData, num_applicants: parseInt(e.target.value) || 1 })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date of Travel (DOT)</label>
                  <input
                    type="date"
                    value={formData.travel_date}
                    onChange={(e) => setFormData({ ...formData, travel_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <select
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  >
                    <option key="select-source" value="">Select Source</option>
                    {sourceOptions.map(source => (
                      <option key={source.value} value={source.value}>{source.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Temperature</label>
                  <select
                    value={formData.temperature}
                    onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                  >
                    {TEMPERATURE_OPTIONS.map(temp => (
                      <option key={temp.value} value={temp.value}>{temp.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                  />
                </div>
                
                {/* Auto Email Toggle - Only show when editing */}
                {selectedLead && (
                  <div className="form-group full-width" style={{ 
                    background: formData.auto_email_enabled ? '#f0fdf4' : '#fef2f2', 
                    padding: '12px 16px', 
                    borderRadius: '8px',
                    border: formData.auto_email_enabled ? '1px solid #86efac' : '1px solid #fecaca'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={formData.auto_email_enabled}
                        onChange={(e) => setFormData({ ...formData, auto_email_enabled: e.target.checked })}
                        data-testid="auto-email-toggle"
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: '600', color: formData.auto_email_enabled ? '#166534' : '#991b1b' }}>
                        {formData.auto_email_enabled ? '✅ Auto Emails Enabled' : '🚫 Auto Emails Disabled'}
                      </span>
                    </label>
                    <small style={{ display: 'block', marginTop: '6px', color: '#6b7280', marginLeft: '28px' }}>
                      {formData.auto_email_enabled 
                        ? 'This lead will receive automated nurturing emails based on their stage.'
                        : 'This lead will NOT receive any automated emails. Manual emails can still be sent.'}
                    </small>
                  </div>
                )}
                
                {/* Blacklist Toggle - Only show when editing, admin/super_admin only */}
                {selectedLead && (admin?.role === 'super_admin' || admin?.role === 'admin') && (
                  <div className="form-group full-width" style={{ 
                    background: selectedLead.is_blacklisted ? '#fef2f2' : '#f9fafb', 
                    padding: '12px 16px', 
                    borderRadius: '8px',
                    border: selectedLead.is_blacklisted ? '2px solid #ef4444' : '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <span style={{ fontWeight: '600', color: selectedLead.is_blacklisted ? '#991b1b' : '#374151', fontSize: '14px' }}>
                          {selectedLead.is_blacklisted ? '🚫 Phone Number Blacklisted' : 'Blacklist Phone Number'}
                        </span>
                        <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                          {selectedLead.is_blacklisted 
                            ? `Phone ${selectedLead.phone} is blacklisted. All future leads from this number will be auto-cancelled.`
                            : 'Blacklisting will cancel this lead and auto-cancel all future leads from this phone number.'}
                        </small>
                      </div>
                      <button
                        type="button"
                        data-testid="blacklist-toggle-btn"
                        onClick={async () => {
                          const action = selectedLead.is_blacklisted ? 'remove from blacklist' : 'blacklist';
                          if (!window.confirm(`Are you sure you want to ${action} phone ${selectedLead.phone}?`)) return;
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/leads/${selectedLead.id}/blacklist`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                              }
                            });
                            if (response.ok) {
                              const data = await response.json();
                              alert(data.message);
                              fetchLeads();
                              setShowCreateModal(false);
                            }
                          } catch (error) {
                            console.error('Blacklist error:', error);
                          }
                        }}
                        style={{
                          padding: '6px 16px',
                          background: selectedLead.is_blacklisted ? '#16a34a' : '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {selectedLead.is_blacklisted ? 'Remove Blacklist' : 'Blacklist'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {selectedLead ? 'Update Lead' : 'Create Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <h2>Lead Details: {selectedLead.name}</h2>
            
            <div className="lead-details">
              <div className="detail-section">
                <h3>Contact Information</h3>
                <p><strong>Email:</strong> {selectedLead.email}</p>
                <p><strong>Phone:</strong> {selectedLead.phone}</p>
              </div>

              <div className="detail-section">
                <h3>Service Details</h3>
                <p><strong>Service Type:</strong> {selectedLead.service_type || 'N/A'}</p>
                <p><strong>Destination:</strong> {selectedLead.destination_country || 'N/A'}</p>
                <p><strong>Source:</strong> {selectedLead.source}</p>
                <p><strong>Temperature:</strong> {selectedLead.temperature}</p>
              </div>

              <div className="detail-section">
                <h3>Stage Management</h3>
                <p><strong>Current Stage:</strong> {selectedLead.stage}</p>
                <select
                  onChange={(e) => handleUpdateStage(selectedLead.id, e.target.value)}
                  className="stage-selector"
                  value={selectedLead.stage}
                >
                  {filteredStageOptions.map(stage => (
                    <option 
                      key={stage.value} 
                      value={stage.value}
                      disabled={stage.isReadOnly}
                    >
                      {stage.label} {stage.isReadOnly ? '(View Only)' : ''}
                    </option>
                  ))}
                </select>
                {userRolePermissions.viewable_stages.includes(selectedLead.stage) && 
                 !userRolePermissions.accessible_stages.includes(selectedLead.stage) && (
                  <p style={{ color: '#f59e0b', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    ⚠️ You can only view this stage, not edit it
                  </p>
                )}
              </div>

              <div className="detail-section">
                <h3>Payment Information</h3>
                <p><strong>Total Amount:</strong> ₹{selectedLead.payment_info?.total_amount || 0}</p>
                <p><strong>Amount Paid:</strong> ₹{selectedLead.payment_info?.amount_paid || 0}</p>
                <p><strong>Balance:</strong> ₹{selectedLead.payment_info?.balance || 0}</p>
                <p><strong>Status:</strong> {selectedLead.payment_info?.payment_status || 'unpaid'}</p>
                <button className="btn-primary" onClick={() => setShowPaymentModal(true)}>
                  Add Payment
                </button>
              </div>

              {selectedLead.stage_history && selectedLead.stage_history.length > 0 && (
                <div className="detail-section">
                  <h3>Stage History</h3>
                  <div className="stage-history">
                    {selectedLead.stage_history.map((history, index) => (
                      <div key={index} className="history-item">
                        <strong>{history.stage}</strong> - {new Date(history.changed_at).toLocaleString()}
                        <br />
                        <span className="text-muted">By: {history.changed_by}</span>
                        {history.notes && <p>{history.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLead.stage === 'payment_received' && !selectedLead.converted_to_client && (
                <div className="detail-section">
                  <button
                    className="btn-success"
                    onClick={() => handleConvertToClient(selectedLead.id)}
                  >
                    Convert to Client
                  </button>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Payment</h2>
            <form onSubmit={handleAddPayment}>
              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                >
                  <option key="select-method" value="">Select Method</option>
                  <option key="cash" value="cash">Cash</option>
                  <option key="card" value="card">Card</option>
                  <option key="bank_transfer" value="bank_transfer">Bank Transfer</option>
                  <option key="upi" value="upi">UPI</option>
                  <option key="cheque" value="cheque">Cheque</option>
                </select>
              </div>
              <div className="form-group">
                <label>Transaction ID</label>
                <input
                  type="text"
                  value={paymentData.transaction_id}
                  onChange={(e) => setPaymentData({ ...paymentData, transaction_id: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowPaymentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Add Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Lead Modal */}
      {showUpdateModal && selectedLead && (
        <UpdateLeadModal
          lead={selectedLead}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedLead(null);
          }}
          onUpdate={async () => {
            // Refresh the selected lead data FIRST to show latest updates
            if (selectedLead && selectedLead.id) {
              try {
                const response = await fetch(`${BACKEND_URL}/api/leads/${selectedLead.id}`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                  }
                });
                if (response.ok) {
                  const updatedLead = await response.json();
                  setSelectedLead(updatedLead);
                }
              } catch (error) {
                console.error('Error refreshing lead:', error);
              }
            }
            // Refresh leads list in background without affecting modal
            await fetchLeads();
            // Don't close modal - let user continue working
          }}
          getAuthHeader={getAuthHeader}
        />
      )}

      {/* Assignment Modal */}
      {showAssignModal && assigningLead && (
        <AssignmentModal
          lead={assigningLead}
          users={availableUsers}
          onClose={() => {
            setShowAssignModal(false);
            setAssigningLead(null);
          }}
          onAssign={handleAssignLead}
        />
      )}

      {/* Pricing Suggestion Modal */}
      {showPricingModal && selectedLead && (
        <PricingSuggestionModal
          lead={selectedLead}
          onClose={() => {
            setShowPricingModal(false);
            setSelectedLead(null);
          }}
        />
      )}
      </div>
    </div>
  );
}

// Assignment Modal Component
function AssignmentModal({ lead, users, onClose, onAssign }) {
  const [selectedUser, setSelectedUser] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedUser) {
      alert('Please select a user');
      return;
    }
    onAssign(selectedUser, reason);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content assign-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Assign Lead</h2>
        <div className="lead-info">
          <p><strong>{lead.name}</strong></p>
          <p>{lead.email} | {lead.phone}</p>
          <p>{lead.service_type} - {lead.destination_country}</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Assign To *</label>
            <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} required>
              <option key="select-user" value="">Select User</option>
              {users.map(user => (
                <option key={user.username} value={user.username}>
                  {user.username} ({user.role}) - {user.email}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Reason</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for assignment..."
              rows="3"
            />
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Assign Lead</button>
          </div>
        </form>
      </div>
    </div>
  );
}

